import React, { useEffect, useMemo, useState, useCallback } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
	IconQrcode,
	IconDeviceMobile,
	IconChefHat,
	IconUsers,
	IconToggleLeft,
	IconBuildingStore,
} from "@tabler/icons-react";
import DashboardLayout from "../hocs/layout/DashboardLayout";
import { Meta } from "../components/navigation/Helmet";
import tableChatLogo from "../assets/img/icon.png";
import axios from "axios";
import CreateBusinessModal from "../components/business/CreateBusinessModal";
import Toast from "../components/Alert";

// ===== Utils
const API = "/api";
const getToken = (reduxAccess) =>
	reduxAccess || localStorage.getItem("access") || "";
const API_URL = process.env.REACT_APP_API_URL || "";

// ===== Componente
function Dashboard({ user, access }) {
	const [dashboardData, setDashboardData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [errMsg, setErrMsg] = useState("");
	const [showCreateBusinessModal, setShowCreateBusinessModal] = useState(false);

	const navigate = useNavigate();

	// ---- Fetch principal del dashboard
	const fetchDashboardData = useCallback(async () => {
		setLoading(true);
		setErrMsg("");
		try {
			const token = getToken(access);
			if (!token) {
				setErrMsg("No hay token de autenticación.");
				setLoading(false);
				return;
			}
			const res = await axios.get(`${API_URL}${API}/dashboard/`, {
				headers: { Authorization: `JWT ${token}`, Accept: "application/json" },
			});
			setDashboardData(res.data);
		} catch (e) {
			console.error(e);
			const errorMessage =
				e?.response?.data?.detail ||
				e?.response?.data?.error ||
				e?.message ||
				"Error al cargar el dashboard";
			setErrMsg(errorMessage);
		} finally {
			setLoading(false);
		}
	}, [access]);

	useEffect(() => {
		fetchDashboardData();
	}, [fetchDashboardData]);

	// ---- Crear/actualizar borrador de perfil BUSINESS (upsert)
	const upsertBusinessDraft = async (formData) => {
		try {
			const token = getToken(access);
			const res = await axios.post(`${API_URL}${API}/business/my/`, formData, {
				headers: {
					Authorization: `JWT ${token}`,
					"Content-Type": "application/json",
				},
			});
			await fetchDashboardData();
			Toast({
				tipo: "success",
				mensaje: "Perfil guardado en borrador. Complétalo y actívalo.",
			});
			return res.data;
		} catch (e) {
			const msg = e?.response?.data?.error || "No se pudo guardar el perfil.";
			Toast({ tipo: "error", mensaje: msg });
		}
	};

	// ---- Activar perfil (valida campos mínimos en backend)
	const activateBusiness = async () => {
		try {
			const token = getToken(access);
			await axios.post(
				`${API_URL}${API}/business/activate/`,
				{},
				{
					headers: {
						Authorization: `JWT ${token}`,
						"Content-Type": "application/json",
					},
				}
			);
			await fetchDashboardData();
			Toast({ tipo: "success", mensaje: "¡Perfil activado!" });
		} catch (e) {
			const rd = e?.response?.data;
			const missing = rd?.missing?.length ? `: ${rd.missing.join(", ")}` : "";
			const errorMessage = rd?.error
				? `${rd.error}${missing}`
				: "Error al activar el perfil.";
			Toast({ tipo: "error", mensaje: errorMessage });
		}
	};

	// ---- Abrir modal para crear perfil
	const handleCreateBusiness = () => setShowCreateBusinessModal(true);

	// ---- Acciones rápidas (según estado)
	const actions = useMemo(() => {
		const list = [];

		const utype = dashboardData?.user?.user_type;
		const business = dashboardData?.business;
		const profileComplete = Boolean(dashboardData?.profile_complete);

		if (utype === "NORMAL") {
			list.push({
				title: "Activar Negocio",
				description: "Crear perfil de restaurante",
				icon: IconToggleLeft,
				color: "bg-green-500",
				action: handleCreateBusiness,
			});
		}

		if (utype === "BUSINESS") {
			if (!business) {
				list.push({
					title: "Crear Perfil de Negocio",
					description: "No tienes perfil. Créalo ahora.",
					icon: IconBuildingStore,
					color: "bg-red-500",
					action: handleCreateBusiness,
				});
			} else if (business && !business.is_active) {
				list.push({
					title: "Activar Restaurante",
					description: "Valida y activa tu perfil",
					icon: IconToggleLeft,
					color: "bg-yellow-500",
					action: activateBusiness,
				});
			} else if (business && business.is_active && profileComplete) {
				list.push(
					{
						title: "Mi Restaurante",
						description: "Configurar información del negocio",
						icon: IconBuildingStore,
						color: "bg-purple-500",
						action: () => navigate("/mi-restaurante"),
					},
					{
						title: "Ver Mesas",
						description: "Gestiona mesas y ve órdenes",
						icon: IconQrcode,
						color: "bg-indigo-500",
						action: () => navigate("/mesas"),
					}
				);
			}
		}

		return list;
	}, [dashboardData, navigate]);

	// ===== UI

	// Loader
	if (loading) {
		return (
			<DashboardLayout>
				<Meta title="Dashboard" />
				<div className="flex justify-center items-center h-64">
					<div className="animate-spin rounded-full h-12 w-12 border-2 border-gray-200 border-t-[#ff1049]" />
				</div>
			</DashboardLayout>
		);
	}

	// Error
	if (errMsg) {
		return (
			<DashboardLayout>
				<Meta title="Dashboard" />
				<div className="max-w-xl mx-auto mt-10">
					<div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
						<p className="font-semibold mb-1">No se pudo cargar el dashboard</p>
						<p className="text-sm">{errMsg}</p>
						<button
							onClick={fetchDashboardData}
							className="mt-4 inline-flex items-center justify-center rounded-lg px-4 py-2 text-white font-semibold
                         bg-gradient-to-r from-[#ff1049] to-[#ff7826] hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[#ff1049]/40"
						>
							Reintentar
						</button>
					</div>
				</div>
			</DashboardLayout>
		);
	}

	// Content
	return (
		<DashboardLayout>
			<Meta title="Dashboard" />

			{/* Header */}
			<div className="mb-8">
				<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
					<div>
						<h1 className="text-3xl font-bold text-gray-900 mb-2">
							¡Hola, {dashboardData?.user?.first_name || "Usuario"}! 👋
						</h1>
						<p className="text-gray-600">
							{dashboardData?.business
								? `Administra tu restaurante ${dashboardData.business.business_name}`
								: "Bienvenido a TableChat — Gestión de restaurantes sin meseros"}
						</p>
					</div>
					<div className="mt-1 sm:mt-0">
						<div className="bg-white rounded-xl shadow-sm border p-4 flex items-center gap-3">
							<img
								src={tableChatLogo}
								className="w-12 h-12 rounded-full"
								alt="TableChat Logo"
							/>
							<div>
								<p className="text-xs text-gray-500">Conectando mesas con</p>
								<p className="font-bold text-gray-900">TableChat</p>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Estado del Perfil de Negocio */}
			{dashboardData?.user?.user_type === "BUSINESS" && (
				<div className="mb-8">
					<div
						className={`rounded-xl p-5 border ${
							dashboardData?.profile_complete
								? "bg-green-50 border-green-200"
								: dashboardData?.business && !dashboardData?.business?.is_active
								? "bg-yellow-50 border-yellow-200"
								: dashboardData?.business
								? "bg-blue-50 border-blue-200"
								: "bg-white border-gray-200"
						}`}
					>
						<div className="flex items-start gap-3">
							<div
								className={`p-2 rounded-full ${
									dashboardData?.profile_complete
										? "bg-green-100"
										: dashboardData?.business &&
										  !dashboardData?.business?.is_active
										? "bg-yellow-100"
										: "bg-blue-100"
								}`}
							>
								<IconBuildingStore
									className={`h-5 w-5 ${
										dashboardData?.profile_complete
											? "text-green-600"
											: dashboardData?.business &&
											  !dashboardData?.business?.is_active
											? "text-yellow-600"
											: "text-blue-600"
									}`}
								/>
							</div>
							<div>
								<h3
									className={`font-semibold ${
										dashboardData?.profile_complete
											? "text-green-800"
											: dashboardData?.business &&
											  !dashboardData?.business?.is_active
											? "text-yellow-800"
											: dashboardData?.business
											? "text-blue-800"
											: "text-gray-800"
									}`}
								>
									{dashboardData?.profile_complete
										? "¡Perfil de Restaurante Activo!"
										: dashboardData?.business &&
										  !dashboardData?.business?.is_active
										? "Perfil Creado — Actívalo ahora"
										: dashboardData?.business
										? "Completa tu Perfil de Restaurante"
										: "Crea tu Perfil de Negocio"}
								</h3>

								<p
									className={`text-sm mt-0.5 ${
										dashboardData?.profile_complete
											? "text-green-600"
											: dashboardData?.business &&
											  !dashboardData?.business?.is_active
											? "text-yellow-600"
											: dashboardData?.business
											? "text-blue-600"
											: "text-gray-600"
									}`}
								>
									{dashboardData?.profile_complete
										? "Tu restaurante está listo para recibir pedidos."
										: dashboardData?.business &&
										  !dashboardData?.business?.is_active
										? "Completa la información requerida y actívalo."
										: dashboardData?.business
										? "Agrega la información básica de tu restaurante."
										: "Haz clic en “Activar Negocio” para comenzar."}
								</p>

								{!!dashboardData?.missing_fields?.length && (
									<p className="text-xs text-gray-600 mt-1">
										Campos faltantes para activar:{" "}
										{dashboardData.missing_fields.join(", ")}
									</p>
								)}

								{/* CTA contextuales */}
								<div className="mt-3 flex flex-wrap gap-2">
									{!dashboardData?.business && (
										<button
											onClick={handleCreateBusiness}
											className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-white text-sm font-semibold
                                 bg-gradient-to-r from-[#ff1049] to-[#ff7826] hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[#ff1049]/40"
										>
											Activar Negocio
										</button>
									)}
									{dashboardData?.business &&
										!dashboardData?.business?.is_active && (
											<button
												onClick={activateBusiness}
												className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-white text-sm font-semibold
                                 bg-gradient-to-r from-[#ff1049] to-[#ff7826] hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[#ff1049]/40"
											>
												Activar Restaurante
											</button>
										)}
									{dashboardData?.business && (
										<button
											onClick={handleCreateBusiness}
											className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold border border-gray-300 bg-white hover:bg-gray-50"
										>
											Editar Perfil
										</button>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Estadísticas del Negocio */}
			{dashboardData?.business &&
				dashboardData?.business?.is_active &&
				dashboardData?.profile_complete &&
				dashboardData?.user?.user_type === "BUSINESS" && (
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
						{/* Card 1 */}
						<div className="bg-white rounded-xl shadow-sm border p-6">
							<div className="flex items-center">
								<div className="p-3 bg-blue-100 rounded-full">
									<IconChefHat className="h-6 w-6 text-blue-600" />
								</div>
								<div className="ml-4">
									<p className="text-sm font-medium text-gray-600">
										Archivos de Menú
									</p>
									<p className="text-2xl font-semibold text-gray-900">
										{dashboardData.menu_files_count || 0}
									</p>
								</div>
							</div>
						</div>
						{/* Card 2 */}
						<div className="bg-white rounded-xl shadow-sm border p-6">
							<div className="flex items-center">
								<div className="p-3 bg-green-100 rounded-full">
									<IconUsers className="h-6 w-6 text-green-600" />
								</div>
								<div className="ml-4">
									<p className="text-sm font-medium text-gray-600">Imágenes</p>
									<p className="text-2xl font-semibold text-gray-900">
										{dashboardData.images_count || 0}
									</p>
								</div>
							</div>
						</div>
						{/* Card 3 */}
						<div className="bg-white rounded-xl shadow-sm border p-6">
							<div className="flex items-center">
								<div className="p-3 bg-purple-100 rounded-full">
									<IconBuildingStore className="h-6 w-6 text-purple-600" />
								</div>
								<div className="ml-4">
									<p className="text-sm font-medium text-gray-600">Tipo</p>
									<p className="text-lg font-semibold text-gray-900">
										{dashboardData.business?.business_type || "N/A"}
									</p>
								</div>
							</div>
						</div>
						{/* Card 4 */}
						<div className="bg-white rounded-xl shadow-sm border p-6">
							<div className="flex items-center">
								<div
									className={`p-3 rounded-full ${
										dashboardData.profile_complete
											? "bg-green-100"
											: "bg-orange-100"
									}`}
								>
									<IconDeviceMobile
										className={`h-6 w-6 ${
											dashboardData.profile_complete
												? "text-green-600"
												: "text-orange-600"
										}`}
									/>
								</div>
								<div className="ml-4">
									<p className="text-sm font-medium text-gray-600">Estado</p>
									<p className="text-lg font-semibold text-gray-900">
										{dashboardData.profile_complete ? "Activo" : "Pendiente"}
									</p>
								</div>
							</div>
						</div>
					</div>
				)}

			{/* Acciones Rápidas */}
			<div className="mb-10">
				<h2 className="text-2xl font-semibold text-gray-900 mb-6">
					Acciones Disponibles
				</h2>
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
					{actions.map((action, i) => {
						const IconComponent = action.icon;
						return (
							<button
								key={i}
								onClick={action.action}
								className="text-left bg-white rounded-xl shadow-sm border hover:shadow-md transition-shadow cursor-pointer group"
							>
								<div className="p-6">
									<div
										className={`inline-flex p-3 rounded-full ${action.color} mb-4 group-hover:scale-110 transition-transform`}
									>
										<IconComponent className="h-6 w-6 text-white" />
									</div>
									<h3 className="text-lg font-semibold text-gray-900 mb-1">
										{action.title}
									</h3>
									<p className="text-gray-600 text-sm">{action.description}</p>
								</div>
							</button>
						);
					})}
				</div>
			</div>

			{/* Intro si NO hay negocio */}
			{!dashboardData?.business && (
				<div className="bg-white rounded-xl border shadow-sm p-8 mb-10">
					<div className="max-w-3xl">
						<h2 className="text-2xl font-bold text-gray-900 mb-4">
							¿Cómo funciona TableChat?
						</h2>
						<div className="space-y-4">
							{[
								[
									"Activa tu perfil de negocio",
									"Si administras un restaurante, activa tu perfil para comenzar.",
								],
								[
									"Configura tu restaurante",
									"Agrega información, menú y fotos de tu negocio.",
								],
								[
									"Genera códigos QR",
									"Coloca códigos QR en tus mesas para pedidos directos.",
								],
								[
									"Recibe pedidos sin meseros",
									"Los clientes ordenan desde sus dispositivos.",
								],
							].map(([title, desc], idx) => (
								<div key={idx} className="flex items-start gap-3">
									<div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-r from-[#ff1049] to-[#ff7826] text-white flex items-center justify-center text-sm font-bold">
										{idx + 1}
									</div>
									<div>
										<h3 className="font-semibold text-gray-900">{title}</h3>
										<p className="text-gray-700">{desc}</p>
									</div>
								</div>
							))}
						</div>

						<button
							onClick={handleCreateBusiness}
							className="mt-6 inline-flex items-center justify-center rounded-lg px-5 py-3 text-white font-semibold
                         bg-gradient-to-r from-[#ff1049] to-[#ff7826] hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[#ff1049]/40"
						>
							Activar Negocio
						</button>
					</div>
				</div>
			)}

			{/* Modal para crear perfil de negocio */}
			<CreateBusinessModal
				isOpen={showCreateBusinessModal}
				onClose={() => setShowCreateBusinessModal(false)}
				onSubmit={upsertBusinessDraft}
				user={user}
			/>
		</DashboardLayout>
	);
}

const mapStateToProps = (state) => ({
	user: state.auth.user,
	access: state.auth.access,
});

export default connect(mapStateToProps)(Dashboard);
